/** The inspector beside the outline (plan §5): the selected stop's fields and its activity
 *  rows, or the selected day's fields. Its grid is pinned to the column count the
 *  inspector has in EDIT mode (beside the docked Changes), so pressing E moves no field to
 *  another row; a field shown only for some values (the timeline label, the transport)
 *  comes after the permanent ones of its row. */
import AddIcon from '@mui/icons-material/Add';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import MoveDownIcon from '@mui/icons-material/MoveDown';
import { Alert, Box, Button, Divider, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import { memo, useState, type ReactNode } from 'react';

import { useDraftStable } from '@/features/records/draft/DraftProvider';
import { RestoreButton } from '@/features/records/fields/RowMarks';
import { useDraftSelector, useOutlineField } from '@/features/records/outline/store';

import { ActivityList } from './ActivityList';
import { ItineraryFieldSlot, type FieldSpec } from './fields';
import { dayRec, DAYS_PATH, hhmm, htmlText, itineraryTree, listIn, placementOf, slotRec, TOUR_KML, valueIn } from './model';
import { useItinerary } from './store';
import { addSlot, moveDay, moveSlotToDay, removeDay, removeSlot, restoreDay, restoreSlot } from './structure';
import { useStructure } from './useStructure';

/** Module constants: a field's props stay equal across renders (its memo holds). */
const SPEC = {
  time: { kind: 'time' },
  text256: { kind: 'text', maxLength: 256 },
  text128: { kind: 'text', maxLength: 128 },
  description: { kind: 'richtext', placeholder: 'Empty — click to write' },
  timeline: { kind: 'timeline' },
  showTransport: { kind: 'switch', on: 'Shown', off: 'Hidden' },
  transport: { kind: 'picker', picker: 'transport_types', errorText: 'Couldn’t load the transport types' },
  integer: { kind: 'integer' },
  kml: { kind: 'kml' },
} as const satisfies Record<string, FieldSpec>;

const PIN_SPECS = new Map<number | null, FieldSpec>();
const pinSpec = (position: number | null): FieldSpec => {
  let spec = PIN_SPECS.get(position);
  if (!spec) {
    spec = { kind: 'pin', position };
    PIN_SPECS.set(position, spec);
  }
  return spec;
};

function grid(columns: number) {
  return { display: 'grid', columnGap: 2, gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` } as const;
}

function Header({ eyebrow, title, actions }: { eyebrow: string; title: ReactNode; actions: ReactNode }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, minHeight: 48 }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary">
          {eyebrow}
        </Typography>
        <Typography variant="subtitle1" component="h3" noWrap sx={{ fontWeight: 600, m: 0 }}>
          {title}
        </Typography>
      </Box>
      {/* The actions' room is kept in view mode. */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 'none', minHeight: 32 }}>{actions}</Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// A stop
// ---------------------------------------------------------------------------

function SlotTitle({ slotId }: { slotId: number }) {
  const rec = slotRec(slotId);
  const time = useOutlineField(rec, 'slot.time').value;
  const title = useOutlineField(rec, 'slot.title').value;
  return (
    <>
      <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums' }}>
        {typeof time === 'string' && time ? hhmm(time) : '—'}
      </Box>{' '}
      {typeof title === 'string' && title.trim() ? title.trim() : <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>Untitled stop</Box>}
    </>
  );
}

export const SlotInspector = memo(function SlotInspector({ slotId, columns }: { slotId: number; columns: number }) {
  const { editing, canEdit, baseline, tourRec } = useItinerary();
  const live = editing && canEdit;
  const rec = slotRec(slotId);
  const run = useStructure();
  const place = useDraftSelector(
    (entries) => placementOf(itineraryTree(entries, baseline.paths, tourRec)).slots.get(slotId) ?? null,
    (p) => (p ? `${p.dayId}:${p.dayIndex}:${p.slotIndex}` : ''),
  );
  const display = useOutlineField(rec, 'slot.timeline_display').value;
  const showTransport = useOutlineField(rec, 'slot.show_transport').value === true;
  const [moveAnchor, setMoveAnchor] = useState<HTMLElement | null>(null);
  const storedDay = useStoredDayOf(slotId);

  if (!place) {
    return (
      <Alert
        severity="warning"
        action={
          live && storedDay !== null ? (
            <RestoreButton name="this stop" hint="keep this stop" onRestore={() => run((c) => restoreSlot(c, slotId, storedDay))} />
          ) : undefined
        }
      >
        This stop is removed — it is deleted when you save.
      </Alert>
    );
  }

  return (
    <Box data-itin-inspector="slot" data-rec={rec} sx={{ display: 'grid', gap: 1 }}>
      <Header
        eyebrow={`Day ${place.dayIndex + 1} · Stop ${place.slotIndex + 1}`}
        title={<SlotTitle slotId={slotId} />}
        actions={
          live ? (
            <>
              <Button size="small" startIcon={<MoveDownIcon />} aria-haspopup="menu" onClick={(e) => setMoveAnchor(e.currentTarget)}>
                Move to day
              </Button>
              <Button size="small" color="error" startIcon={<DeleteOutlineIcon />} onClick={() => run((c) => removeSlot(c, slotId))}>
                Remove
              </Button>
            </>
          ) : null
        }
      />
      {moveAnchor && <MoveToDayMenu anchor={moveAnchor} slotId={slotId} dayId={place.dayId} onClose={() => setMoveAnchor(null)} />}
      <Box sx={grid(columns)}>
        <ItineraryFieldSlot rec={rec} path="slot.time" spec={SPEC.time} />
        <ItineraryFieldSlot rec={rec} path="slot.pin" spec={pinSpec(place.slotIndex + 1)} />
        <ItineraryFieldSlot rec={rec} path="slot.title" spec={SPEC.text256} wide />
        <ItineraryFieldSlot rec={rec} path="slot.subtitle" spec={SPEC.text256} wide />
        <ItineraryFieldSlot rec={rec} path="slot.description" spec={SPEC.description} wide />
        <ItineraryFieldSlot rec={rec} path="slot.timeline_display" spec={SPEC.timeline} />
        {display === 0 && <ItineraryFieldSlot rec={rec} path="slot.timeline_text" spec={SPEC.text256} />}
        <Box sx={{ gridColumn: '1 / -1' }} />
        <ItineraryFieldSlot rec={rec} path="slot.show_transport" spec={SPEC.showTransport} />
        {showTransport && <ItineraryFieldSlot rec={rec} path="slot.next_transport" spec={SPEC.transport} />}
        {showTransport && <ItineraryFieldSlot rec={rec} path="slot.transport_text" spec={SPEC.text128} wide />}
      </Box>
      <Divider />
      <ActivityList slotId={slotId} columns={columns} />
    </Box>
  );
});

/** The saved day a stop is stored in (for Restore), or null. */
function useStoredDayOf(slotId: number): number | null {
  const { baseline, tourRec } = useItinerary();
  for (const dayId of listIn(null, baseline.paths, tourRec, DAYS_PATH)) {
    const slots = baseline.paths[dayRec(dayId)]?.['day.slots'];
    if (Array.isArray(slots) && (slots as number[]).includes(slotId)) return dayId;
  }
  return null;
}

function MoveToDayMenu({ anchor, slotId, dayId, onClose }: { anchor: HTMLElement; slotId: number; dayId: number; onClose: () => void }) {
  const { baseline, tourRec } = useItinerary();
  const { store } = useDraftStable();
  const run = useStructure();
  const [days] = useState(() => {
    const entries = store.getState().entries;
    return listIn(entries, baseline.paths, tourRec, DAYS_PATH).map((id, index) => ({
      id,
      index,
      name: htmlText(valueIn(entries, baseline.paths, dayRec(id), 'day.title')),
    }));
  });
  return (
    <Menu anchorEl={anchor} open onClose={onClose}>
      {days.map((d) => (
        <MenuItem
          key={d.id}
          disabled={d.id === dayId}
          onClick={() => {
            onClose();
            run((c) => moveSlotToDay(c, slotId, d.id));
          }}
        >
          Day {d.index + 1}
          {d.name ? ` · ${d.name}` : ''}
          {slotId > 0 && d.id < 0 ? ' — save it first' : ''}
        </MenuItem>
      ))}
    </Menu>
  );
}

// ---------------------------------------------------------------------------
// A day
// ---------------------------------------------------------------------------

function DayTitle({ dayId }: { dayId: number }) {
  const title = useOutlineField(dayRec(dayId), 'day.title').value;
  return typeof title === 'string' && title.trim() ? <>{title.trim()}</> : <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>Untitled day</Box>;
}

/** "Mon 6 Oct – Tue 7 Oct" for a fixed-date tour's day (the start date plus the spans of
 *  the days before it). */
function dayDates(startDate: string | null, spans: number[], index: number): string | null {
  if (!startDate) return null;
  const base = new Date(`${startDate}T00:00:00`);
  if (Number.isNaN(base.getTime())) return null;
  const offset = spans.slice(0, index).reduce((a, n) => a + Math.max(1, n), 0);
  const start = new Date(base);
  start.setDate(start.getDate() + offset);
  const end = new Date(start);
  end.setDate(end.getDate() + Math.max(1, spans[index] ?? 1) - 1);
  const f = (d: Date) => d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
  return f(start) === f(end) ? f(start) : `${f(start)} – ${f(end)}`;
}

export const DayInspector = memo(function DayInspector({ dayId, columns, onCreated }: { dayId: number; columns: number; onCreated: (slotId: number) => void }) {
  const { editing, canEdit, baseline, tourRec, isBespoke, startDate } = useItinerary();
  const live = editing && canEdit;
  const rec = dayRec(dayId);
  const run = useStructure();
  const place = useDraftSelector(
    (entries) => {
      const days = listIn(entries, baseline.paths, tourRec, DAYS_PATH);
      const index = days.indexOf(dayId);
      if (index < 0) return null;
      const spans = days.map((d) => {
        const v = valueIn(entries, baseline.paths, dayRec(d), 'day.day_span');
        return typeof v === 'number' ? v : 1;
      });
      return { index, count: days.length, dates: isBespoke ? dayDates(startDate, spans, index) : null };
    },
    (p) => (p ? `${p.index}/${p.count}/${p.dates ?? ''}` : ''),
  );

  if (!place) {
    return (
      <Alert
        severity="warning"
        action={
          live && dayId > 0 ? (
            <RestoreButton name="this day" hint="keep this day and its stops" onRestore={() => run((c) => restoreDay(c, dayId))} />
          ) : undefined
        }
      >
        This day is removed — it is deleted with its stops when you save.
      </Alert>
    );
  }

  return (
    <Box data-itin-inspector="day" data-rec={rec} sx={{ display: 'grid', gap: 1 }}>
      <Header
        eyebrow={`Day ${place.index + 1}${place.dates ? ` · ${place.dates}` : ''}`}
        title={<DayTitle dayId={dayId} />}
        actions={
          live ? (
            <>
              <Tooltip title="Move up">
                <span>
                  <Button size="small" aria-label="Move this day up" disabled={place.index === 0} onClick={() => run((c) => moveDay(c, dayId, place.index - 1))} sx={{ minWidth: 0 }}>
                    <ArrowUpwardIcon fontSize="small" />
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title="Move down">
                <span>
                  <Button size="small" aria-label="Move this day down" disabled={place.index === place.count - 1} onClick={() => run((c) => moveDay(c, dayId, place.index + 1))} sx={{ minWidth: 0 }}>
                    <ArrowDownwardIcon fontSize="small" />
                  </Button>
                </span>
              </Tooltip>
              <Button size="small" color="error" startIcon={<DeleteOutlineIcon />} onClick={() => run((c) => removeDay(c, dayId))}>
                Remove
              </Button>
            </>
          ) : null
        }
      />
      <Box sx={grid(columns)}>
        <ItineraryFieldSlot rec={rec} path="day.title" spec={SPEC.text256} wide />
        <ItineraryFieldSlot rec={rec} path="day.subtitle" spec={SPEC.text256} wide />
        <ItineraryFieldSlot rec={rec} path="day.day_span" spec={SPEC.integer} />
        <ItineraryFieldSlot rec={rec} path="day.kml" spec={SPEC.kml} wide />
        <ItineraryFieldSlot rec={rec} path="day.description" spec={SPEC.description} wide />
      </Box>
      <Box data-itin-add="slot" sx={{ minHeight: 40, display: 'flex', alignItems: 'center' }}>
        {live && (
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => {
              const id = run((c) => addSlot(c, dayId));
              if (typeof id === 'number') onCreated(id);
            }}
          >
            Add a stop to Day {place.index + 1}
          </Button>
        )}
      </Box>
    </Box>
  );
});

/** The tour-level route file (B6), in the workspace's toolbar. */
export function TourKmlField() {
  const { tourRec } = useItinerary();
  return <ItineraryFieldSlot rec={tourRec} path={TOUR_KML} spec={SPEC.kml} />;
}
